import React, { useMemo } from 'react';
import Modal, { DialogPanel, DialogHeader, DialogFooter, DialogBody } from './ui/Modal';
import { Bell } from 'lucide-react';
import { groupPushNotifications } from '../utils/pushNotificationHistory';
import NotificationStackRow from './NotificationStackRow';

export default function NotificationsHistoryModal({
  open,
  onClose,
  items = [],
  onSelect,
  onMarkAllRead,
}) {
  const stacks = useMemo(
    () => groupPushNotifications(Array.isArray(items) ? items : []),
    [items],
  );
  const eventCount = Array.isArray(items) ? items.length : 0;
  const unreadStacks = stacks.filter((s) => s.unreadCount > 0).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      align="start"
      labelledBy="notifications-history-title"
    >
      <DialogPanel maxWidth="max-w-xl" className="max-h-[min(86vh,720px)] flex flex-col">
        <DialogHeader
          titleId="notifications-history-title"
          title="All notifications"
          subtitle={
            stacks.length
              ? `${stacks.length} threads · ${eventCount} events`
              : 'Push notifications you receive are kept here'
          }
          onClose={onClose}
          tone="muted"
          sticky
        />

        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between gap-2 bg-white shrink-0">
          <p className="text-xs text-gray-500">
            {stacks.length
              ? `${unreadStacks} unread thread${unreadStacks === 1 ? '' : 's'}`
              : 'Saved on this device'}
          </p>
          {eventCount > 0 && stacks.some((s) => s.unreadCount > 0) ? (
            <button
              type="button"
              onClick={onMarkAllRead}
              className="text-xs font-semibold text-teal-700 hover:text-teal-800 whitespace-nowrap"
            >
              Mark all seen
            </button>
          ) : null}
        </div>

        <DialogBody className="!px-0 !py-0">
          {stacks.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                <Bell className="w-6 h-6" strokeWidth={2} aria-hidden="true" />
              </div>
              <p className="text-sm font-medium text-gray-900">No notifications yet</p>
              <p className="text-xs text-gray-500 mt-1 max-w-xs mx-auto">
                When browser push arrives, it will appear here and stay after refresh.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {stacks.map((stack) => (
                <NotificationStackRow
                  key={stack.key}
                  stack={stack}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-white transition-colors"
          >
            Close
          </button>
        </DialogFooter>
      </DialogPanel>
    </Modal>
  );
}
